import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { ApplicationContext } from '../models/application-context.model';
import { ApplicationContextService } from './application-context.service';
import { AuthenticationService, UserCompany } from './auth.service';
import { getUserCompanyId, getVisibleUserCompanies } from '../utils/studies-company.util';

@Injectable({ providedIn: 'root' })
export class StudiesCompanyAutoSelectService {
  constructor(
    private readonly _authService: AuthenticationService,
    private readonly _injector: Injector,
    private readonly _router: Router
  ) {}

  private resetApplicationContext(): void {
    this._injector.get(ApplicationContextService).resetContext();
  }

  tryAutoSelectSingleCompany(
    context: ApplicationContext,
    options?: { navigateTo?: string; navigate?: boolean }
  ): Observable<boolean> {
    const visible = getVisibleUserCompanies(context.userCompanies);
    if (visible.length !== 1) {
      return of(false);
    }
    if (context.currentlyLoggedInUser?.companyId?.trim()) {
      return of(false);
    }

    return this.switchToCompany(visible[0]).pipe(
      tap((switched) => {
        if (switched && options?.navigate !== false) {
          void this._router.navigateByUrl(options?.navigateTo ?? '/');
        }
      }),
      catchError(() => of(false))
    );
  }

  switchToCompany(userCompany: UserCompany): Observable<boolean> {
    const companyId = getUserCompanyId(userCompany);
    if (!companyId) {
      return of(false);
    }
    return this.switchToCompanyIds(userCompany.userId, companyId);
  }

  switchToCompanyIds(userId: string, companyId: string): Observable<boolean> {
    return this._authService.getAuthTokenForCompany(userId, companyId).pipe(
      tap((response) => {
        const accessToken = (response?.accessToken || '').trim();
        if (!accessToken) {
          throw new Error('Identity session required to select a company');
        }
        this._authService.persistAuthData({
          accessToken,
          refreshToken: (response?.refreshToken || '').trim(),
        });
        this.resetApplicationContext();
      }),
      map(() => true),
      catchError(() => of(false))
    );
  }

  /** Scope Studies to the org passed from Clinic / auth handoff when it differs from the session. */
  tryScopeToRequestedCompany(
    context: ApplicationContext,
    requestedCompanyId: string,
    requestedUserId?: string | null
  ): Observable<ApplicationContext> {
    const targetCompanyId = requestedCompanyId?.trim();
    if (!targetCompanyId) {
      return of(context);
    }

    const activeCompanyId =
      context.currentlyLoggedInUser?.companyId?.trim().toLowerCase();
    if (activeCompanyId === targetCompanyId.toLowerCase()) {
      return of(context);
    }

    const userId =
      requestedUserId?.trim() || context.currentlyLoggedInUser?.id?.trim();
    if (!userId) {
      return of(context);
    }

    return this.switchToCompanyIds(userId, targetCompanyId).pipe(
      switchMap((scoped) => {
        if (!scoped) {
          throw new Error('Failed to scope Studies session to requested organization');
        }
        return this._injector.get(ApplicationContextService).context.pipe(take(1));
      })
    );
  }
}
